'use client';

import {
  Fragment, useCallback, useEffect, useRef, useState,
} from 'react';
import { clsx } from 'clsx';
import Slider, { CustomArrowProps } from 'react-slick';
import { Button } from '@components/common/Forms/Button/Button';
import { Modal } from '@components/common/Modal/Modal';
import { ImageBj } from '@components/common/ImageBj/ImageBj';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { MediaGalleryProps } from '@type/media-gallery';
import 'styles/bj_theme/bj_slick_image_gallery.css';
import { PlayIcon } from '@heroicons/react/20/solid';
import { trackEvent, trackEventWithBeacon } from '@root/src/services/tracking';
import { Video } from '../Video/Video';

// Image Gallery Custom Nav Arrows
// ***********************************
const NextArrow = (props: CustomArrowProps) => {
  const { onClick, className } = props;
  return (
    <Button
      color="dark"
      rounding="full"
      className={`absolute right-2 top-1/2 z-2 -translate-y-1/2 bg-opacity-60 !p-2.5 ${className}`}
      onClick={onClick}
    >
      <ChevronRightIcon className="size-5" />
    </Button>
  );
};

const PrevArrow = (props: CustomArrowProps) => {
  const { onClick, className } = props;
  return (
    <Button
      color="dark"
      rounding="full"
      className={`absolute left-2 top-1/2 z-2 -translate-y-1/2 bg-opacity-60 !p-2.5 ${className}`}
      onClick={onClick}
    >
      <ChevronLeftIcon className="size-5" />
    </Button>
  );
};

/**
 * @description Media Gallery component.
 * A component to render an image gallery slideshow, based on `react-slick` library for the slider functionality
 * and Next.js's Image component for optimized image rendering.
 * Images are shown in a modal view when selected.
 * It also contains a thumbnail navigation, displayed below the main image slider for easy navigation between images.
 *
 * - **items** - ImageGalleryItem[]. The items that will be displayed in the gallery. An item is an object with `src`, 'thumbnail' and `alt` props. Required.
 * - **renderItems** - Render prop function. It is used to define how the images are initially rendered outside the modal.
 * This function takes a function as its argument (`handleImageClick`), used to handle image clicks and open the modal (with the selected index). Required.
 * - **withThumbnailNav** - boolean. Whether the thumbnail navigation is displayed. The thumbnails will be displayed below the main image slider.
 *
 *
 * @example
 *
 * // first, defined the images to be displayed in the gallery
 * const images = [
 *   { src: '/path/to/image1.jpg', alt: 'Image 1', thumbnail: '/path/to/thumb1.jpg' },
 *   { src: '/path/to/image2.jpg', alt: 'Image 2', thumbnail: '/path/to/thumb2.jpg' },
 *   // ...
 * ];
 *
 * // defines how the render the images outside the gallery (eg: a grid of thumbnails).
 * // each image thumbnail has an onClick event handler that calls `handleImageClick` with the index of the clicked image.
 * const CustomImageRenderer = (handleImageClick) => (
 *   <div className="image-grid">
 *     {images.map((image, index) => (
 *       <div key={image.src} onClick={() => handleImageClick(index)} className="image-item">
 *         <img src={image.thumbnail} alt={image.alt} className="image-thumbnail" />
 *       </div>
 *     ))}
 *   </div>
 * );
 *
 * <ImageGallery
 *   items={images}
 *   renderItems={CustomImageRenderer}
 *   withThumbnailNav={true}
 *  />
 *
 * @TODO
 * - transform thumbnail navigation in a react-slick carousel in order to be in sync with the main slider.
 */
export const MediaGallery = (props: MediaGalleryProps) => {
  // Destructure props
  const {
    items, withThumbnailNav = true, headerActions, children, onClose, onSlideChange, trackingEventData,
  } = props;

  // State
  // *****************************************
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(undefined);

  // Slider
  // *****************************************
  const sliderRef = useRef<Slider | null>(null);

  // Current video in view
  const currentVideoRef = useRef<HTMLVideoElement>(null);

  // Since the plugin `initialSlider` property is not working properly with next/prev buttons
  // we set an initial slide using the package API
  // Issue is created https://github.com/akiran/react-slick/issues/2207 / https://github.com/akiran/react-slick/issues/2166
  const initSlider = useCallback((ref: Slider | null) => {
    sliderRef.current = ref;
  }, []);

  // Helper variables
  // *****************************************
  const totalItems = items.length;

  // When the HeadlessUI Dialog is opened, it will focus the first element or the item passed as `initialFocus` property.
  // So we need to get the ref of the slide item and pass it to the Dialog component.
  const focusedElementRef = useRef<HTMLDivElement>(null);

  // Slider settings
  // *****************************************
  const sliderSettings = {
    dots: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: false,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    // To fit the slider on the page we subtract the height of the header (60px), the height of the thumbnail (96px) and the gaps (16px/32px) depending on the elements visible
    className: `bj-slick-image-gallery ${withThumbnailNav ? 'h-[calc(100vh-188px)]' : 'h-[calc(100vh-76px)]'}`,
    beforeChange: (currentSlide: number, nextSlide: number) => {
      // Reset video time when changing slides
      if (currentVideoRef.current && currentSlide !== nextSlide) {
        currentVideoRef.current.pause();
        if (items[currentSlide]?.type === 'video' && trackingEventData) {
          trackEvent({
            ...trackingEventData,
            metadata: [
              ...(trackingEventData?.metadata ?? []),
              ...(items[currentSlide]?.trackingMetadata ?? []),
              { key: 'duration', value: (currentVideoRef.current.duration * 1000).toString() },
              { key: 'timeSpentOn', value: (currentVideoRef.current.currentTime * 1000).toString() },
            ],
          });
        }
        currentVideoRef.current.currentTime = 0;
      }

      // Keep current index in sync with the slider's internal state.
      // It is necessary for arrows or thumbnail navigation.
      setCurrentIndex(nextSlide);
    },
    afterChange: () => {
      onSlideChange?.();
      setTimeout(() => {
        if (currentVideoRef.current) { void currentVideoRef.current.play(); }
      }, 100);
    },
  };

  useEffect(() => {
    if (currentIndex === undefined || !sliderRef.current) return;
    sliderRef.current.slickGoTo(currentIndex, true);
  }, [currentIndex]);

  // Close modal
  // *****************************************************
  const closeModal = () => {
    // Before closing the modal, track the time spent on the current video
    if (currentVideoRef.current && currentIndex !== undefined && items[currentIndex]?.type === 'video' && trackingEventData) {
      trackEvent({
        ...trackingEventData,
        metadata: [
          ...(trackingEventData?.metadata ?? []),
          ...(items[currentIndex]?.trackingMetadata ?? []),
          { key: 'duration', value: (currentVideoRef.current.duration * 1000).toString() },
          { key: 'timeSpentOn', value: (currentVideoRef.current.currentTime * 1000).toString() },
        ],
      });
    }

    onClose?.();
    setCurrentIndex(undefined);
  };

  // Open Modal at specific index
  // *****************************************************
  const openModal = (index?: number) => {
    setCurrentIndex(index ?? 0);
  };

  const handlePause = () => {
    sliderRef.current?.slickPause();
    currentVideoRef.current?.pause();
  };

  // Track on pagehide using beacon to ensure it fires during unload
  // *****************************************************
  useEffect(() => {
    const handlePageHide = () => {
      if (currentVideoRef.current && currentIndex !== undefined && items[currentIndex]?.type === 'video' && trackingEventData) {
        trackEventWithBeacon({
          ...trackingEventData,
          metadata: [
            ...(trackingEventData?.metadata ?? []),
            ...(items[currentIndex]?.trackingMetadata ?? []),
            { key: 'duration', value: (currentVideoRef.current.duration * 1000).toString() },
            { key: 'timeSpentOn', value: (currentVideoRef.current.currentTime * 1000).toString() },
          ],
        });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', handlePageHide);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', handlePageHide);
      }
    };
  }, [currentIndex, items, trackingEventData]);

  // Render component
  // *****************************************************
  return (
    <>
      {/* Render children passing the openModal as prop */}
      {children(openModal)}

      {/* Image Gallery Modal */}
      {currentIndex !== undefined && (
        <Modal
          onClose={closeModal}
          size="full"
          className="flex h-screen w-screen flex-col gap-4 !bg-black/50"
          isOpen
          initialFocus={focusedElementRef}
        >
          {/* Modal Header - includes the current image index, actions and the close button */}
          <div className="relative z-2 flex h-15 shrink-0 items-center justify-between p-2">
            {totalItems > 1 && (
              <span className="text-sm text-white/90">
                {`${currentIndex + 1}/${totalItems}`}
              </span>
            )}
            <div className="flex grow items-center justify-end gap-6">
              {
                typeof headerActions === 'function'
                  ? headerActions({
                    openGalleryAtIndex: openModal,
                    closeGallery: closeModal,
                    currentItem: currentIndex,
                    pauseGallery: handlePause,
                  })
                  : headerActions
              }
              <Button color="dark" className="!rounded-full bg-opacity-60 !p-2.5" onClick={closeModal}>
                <XMarkIcon className="size-5" />
              </Button>
            </div>
          </div>

          {/* Media slider */}
          <Slider {...sliderSettings} ref={initSlider}>
            {items.map((item, index) => (
              <div
                className="relative size-full"
                key={item.src}
                ref={index === currentIndex ? focusedElementRef : null}
              >
                {item.type === 'video'
                  ? (
                    <div className="flex size-full items-center justify-center">
                      <div className="relative flex h-full justify-center">
                        {item.src && (
                          <Video
                            videoRef={index === currentIndex ? currentVideoRef : undefined}
                            url={item.src}
                            controls
                            loop={false}
                            classNames="size-full"
                            videoProps={{
                              onEnded: () => {
                                if (items.length - 1 > index) sliderRef.current?.slickNext();
                              },
                            }}
                          />
                        )}
                        {item.caption && (
                          <div className="absolute bottom-20 left-1/2 w-full max-w-xl -translate-x-1/2 rounded-3xl bg-ink/70 px-4 py-2 text-surface md:w-auto dark:bg-surface/70 dark:text-ink">
                            <p>{item.caption}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                  : (
                    <>
                      {item.src && (
                        <ImageBj
                          src={item.src}
                          extractImageName
                          alt={item.alt || `image${index + 1}`}
                          fill
                          className="object-scale-down"
                          quality={90}
                        />
                      )}
                    </>
                  )}
              </div>
            ))}
          </Slider>

          {/* Thumbnails nav */}
          {withThumbnailNav && (
            <div className="flex h-24 shrink-0 flex-nowrap justify-start overflow-auto pb-2">
              <div className="mx-auto flex gap-4 px-3">
                {items.map((item, index) => (
                  <Fragment key={item.src}>
                    {item.type === 'video'
                      ? (
                        <button
                          type="button"
                          className={clsx('relative inline-flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-full border-3', { 'border-surface opacity-50 transition-opacity hover:opacity-100': currentIndex !== index, 'border-secondary/50': currentIndex === index })}
                          onClick={() => {
                            sliderRef.current?.slickGoTo(index);
                          }}
                        >
                          {item.src && (
                            <>
                              <Video url={item.src} controls={false} classNames="size-16 max-w-none" />
                              <PlayIcon className="absolute size-5 text-[#FFFFFF] drop-shadow-icon" />
                            </>
                          )}
                        </button>
                      )
                      : (
                        <button
                          type="button"
                          className={clsx('relative inline-flex w-16 shrink-0 overflow-hidden rounded-md border-2', { 'border-transparent opacity-50 transition-opacity hover:opacity-100': currentIndex !== index, 'border-primary-light': currentIndex === index })}
                          onClick={() => {
                            sliderRef.current?.slickGoTo(index);
                          }}
                        >
                          {item.thumbnail && (
                            <ImageBj
                              src={item.thumbnail}
                              extractImageName
                              alt={item.alt || `image${index + 1}`}
                              className="object-cover"
                              fill
                            />
                          )}
                        </button>
                      )}
                  </Fragment>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};
